import { useMemo } from "react";
import type { SpecificationItemDto } from "generated/models";
import type {
  DrawerImage as ProductDrawerImage,
  ProductDrawerViewModel,
} from "../product-detail-drawer/types";

type BasicProduct = {
  productName?: string | null;
  mfrPartNum?: string | null;
  productDescriptor?: string | null;
  price?: number | null;
};

type DrawerProductDetail = {
  productName?: string | null;
  mfrPartNum?: string | null;
  productDescriptor?: string | null;
  price?: number | null;
  description?: string | null;
  parsedDescriptionsList?: string[] | null;
  category?: string | null;
  mediaClass?: string | null;
  manufacturer?: string | null;
  msrp?: number | null;
  unspsc?: string | null;
  inventoryStatus?: string | null;
  cNetProductId?: string | null;
  canAddToCart?: boolean | null;
  containedInCatalog?: boolean | null;
};

type RequestWithImageFallback = {
  cNetProductId?: string | null;
  deferImages?: boolean | null;
};

type SourceDrawerImage = {
  url?: string | null;
  angle?: string | null;
};

type DrawerDataWithImages<TSpecification> = {
  productDetail?: DrawerProductDetail | null;
  imagesDeferred?: boolean | null;
  images?: SourceDrawerImage[] | null;
  specification?: TSpecification | null;
  fullSpecification?: SpecificationItemDto[] | null;
};

type DrawerDataQueryResult<TDrawerData> = {
  data: TDrawerData | null;
  isLoading: boolean;
  isInitialLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  isFetched: boolean;
};

type DrawerImagesQueryResult = {
  data: SourceDrawerImage[] | null | undefined;
  isInitialLoading: boolean;
};

export type UseGetProductDetailDrawerHook<
  TRequest,
  TSpecification,
  TDrawerData extends DrawerDataWithImages<TSpecification>,
> = (request: TRequest | null) => DrawerDataQueryResult<TDrawerData>;

export type UseGetProductDetailDrawerImagesHook<TRequest> = (
  request: TRequest | null,
  fallbackImages: boolean,
) => DrawerImagesQueryResult;

export interface UseProductDetailDrawerArgs<
  TProduct extends BasicProduct,
  TRequest extends RequestWithImageFallback,
  TSpecification,
  TDrawerData extends DrawerDataWithImages<TSpecification>,
> {
  product: TProduct | null;
  request: TRequest | null;
  shiComBaseUrl?: string | null;
  useGetProductDetailDrawer: UseGetProductDetailDrawerHook<
    TRequest,
    TSpecification,
    TDrawerData
  >;
  useGetProductDetailDrawerImages: UseGetProductDetailDrawerImagesHook<TRequest>;
}

export interface UseProductDetailDrawerResult<TSpecification> {
  isLoading: boolean;
  isError: boolean;
  hasSettledLookup: boolean;
  /** Whether the response carried a catalog record, as opposed to only the basic product. */
  hasProductDetail: boolean;
  isLoadingFallbackImages: boolean;
  enrichedProduct: ProductDrawerViewModel | null;
  images: ProductDrawerImage[];
  specification: TSpecification | undefined;
  fullSpecification: SpecificationItemDto[] | undefined;
  productDetailUrl: string | null;
}

function toDrawerImages(
  images: SourceDrawerImage[] | null | undefined,
  productName: string | null | undefined,
): ProductDrawerImage[] {
  return (images ?? [])
    .filter((image) => !!image.url)
    .map((image) => ({
      url: image.url!,
      alt: `${productName ?? ""} - ${image.angle ?? "view"}`,
    }));
}

function mergeProductDetail<TProduct extends BasicProduct, TSpecification>(
  product: TProduct | null,
  drawerData: DrawerDataWithImages<TSpecification> | null,
): ProductDrawerViewModel | null {
  if (!product) return null;

  const detail = drawerData?.productDetail;
  if (!detail) {
    return {
      productName: product.productName,
      mfrPartNum: product.mfrPartNum,
      productDescriptor: product.productDescriptor,
      price: product.price,
      hasCatalogDetail: false,
    };
  }

  return {
    productName: detail.productName ?? product.productName,
    mfrPartNum: detail.mfrPartNum ?? product.mfrPartNum,
    productDescriptor: detail.productDescriptor ?? product.productDescriptor,
    price: detail.price ?? product.price,
    description: detail.description,
    parsedDescriptionsList: detail.parsedDescriptionsList,
    category: detail.category,
    mediaClass: detail.mediaClass,
    manufacturer: detail.manufacturer,
    msrp: detail.msrp,
    unspsc: detail.unspsc,
    inventoryStatus: detail.inventoryStatus,
    canAddToCart: detail.canAddToCart,
    containedInCatalog: detail.containedInCatalog,
    hasCatalogDetail: true,
  };
}

export function useProductDetailDrawer<
  TProduct extends BasicProduct,
  TRequest extends RequestWithImageFallback,
  TSpecification,
  TDrawerData extends DrawerDataWithImages<TSpecification>,
>({
  product,
  request,
  shiComBaseUrl,
  useGetProductDetailDrawer,
  useGetProductDetailDrawerImages,
}: UseProductDetailDrawerArgs<
  TProduct,
  TRequest,
  TSpecification,
  TDrawerData
>): UseProductDetailDrawerResult<TSpecification> {
  // 1st attempt: the drawer request fetches product details, specification, and a short inline CNet
  // image "peek" (backend PeekCNetImagesAsync). Thumbnail is never fetched inline, so details/specs
  // paint promptly. When the peek does not resolve images the response sets `imagesDeferred`.
  //
  // 2nd attempt (the client retry): only when `imagesDeferred` is true, the UI re-fetches images via
  // the images-only endpoint (full CNet + thumbnail lookup with lax timeouts) while the already-loaded
  // details/spec stay visible. We feed the CNet product id the backend resolved from the catalog on
  // the first response back into the retry, so the retry is at least as capable as the first attempt
  // (it is not limited to whatever id the original request happened to carry).

  const deferredRequest = useMemo(
    () =>
      request !== null ? ({ ...request, deferImages: true } as TRequest) : null,
    [request],
  );

  const {
    data: drawerData,
    isInitialLoading,
    isError,
    isFetching,
    isFetched,
  } = useGetProductDetailDrawer(deferredRequest);

  const resolvedCNetProductId =
    drawerData?.productDetail?.cNetProductId ||
    request?.cNetProductId ||
    undefined;

  const retryRequest = useMemo(
    () =>
      request !== null
        ? ({ ...request, cNetProductId: resolvedCNetProductId } as TRequest)
        : null,
    [request, resolvedCNetProductId],
  );

  const shouldFetchFallbackImages =
    !!retryRequest && !isInitialLoading && drawerData?.imagesDeferred === true;

  const {
    data: fallbackImagesData,
    isInitialLoading: isLoadingFallbackImages,
  } = useGetProductDetailDrawerImages(retryRequest, shouldFetchFallbackImages);

  const enrichedProduct = useMemo(
    () => mergeProductDetail(product, drawerData),
    [product, drawerData],
  );

  const primaryImages = useMemo(
    () => toDrawerImages(drawerData?.images, enrichedProduct?.productName),
    [drawerData?.images, enrichedProduct?.productName],
  );

  const fallbackImages = useMemo(
    () => toDrawerImages(fallbackImagesData, enrichedProduct?.productName),
    [fallbackImagesData, enrichedProduct?.productName],
  );

  const images = useMemo(
    () => (primaryImages.length > 0 ? primaryImages : fallbackImages),
    [primaryImages, fallbackImages],
  );

  const productDetailUrl = useMemo(
    () =>
      enrichedProduct?.productDescriptor && shiComBaseUrl
        ? `${shiComBaseUrl}/product/${enrichedProduct.productDescriptor}`
        : null,
    [enrichedProduct?.productDescriptor, shiComBaseUrl],
  );

  return {
    // isInitialLoading (react-query v4) = isLoading && isFetching: true only on the first fetch with
    // no cached data. Unlike isLoading it is false when enabled: false (avoids the v4 "disabled query
    // is always loading" trap), and unlike isFetching it stays false during background refetches so
    // cached data within staleTime shows instantly without a skeleton flash.
    isLoading: isInitialLoading,
    isError,
    hasSettledLookup: isFetched && !isFetching,
    hasProductDetail: drawerData?.productDetail != null,
    isLoadingFallbackImages,
    enrichedProduct,
    images,
    specification: drawerData?.specification ?? undefined,
    fullSpecification: drawerData?.fullSpecification ?? undefined,
    productDetailUrl,
  };
}
